import { MapPin, SlidersHorizontal } from 'lucide-react';
import NativeSearchBar from '../NativeSearchBar';
import FilterBottomSheet from '../FilterBottomSheet';
import { useTracking } from '../../hooks/useTracking';
import type { ItemType } from '../../config/itemConfig';
import type { ItemFilters, LocationCoords } from '../../utils/itemListHelpers';

interface ItemListFiltersProps {
  isDesktop: boolean;
  itemType: ItemType;
  config: { singular: string; plural: string };
  // Filter state
  filters: ItemFilters;
  setFilters: React.Dispatch<React.SetStateAction<ItemFilters>>;
  location: { lat: number; lng: number } | null;
  setLocation: React.Dispatch<React.SetStateAction<{ lat: number; lng: number } | null>>;
  locationStatus: string;
  setLocationStatus: React.Dispatch<React.SetStateAction<string>>;
  radius: number;
  setRadius: React.Dispatch<React.SetStateAction<number>>;
  useLocation: boolean;
  setUseLocation: React.Dispatch<React.SetStateAction<boolean>>;
  useFixedLocation: boolean;
  setUseFixedLocation: React.Dispatch<React.SetStateAction<boolean>>;
  availableOnly: boolean;
  setAvailableOnly: React.Dispatch<React.SetStateAction<boolean>>;
  statusFilter: string;
  setStatusFilter: React.Dispatch<React.SetStateAction<string>>;
  // UI state
  showFilters: boolean;
  setShowFilters: React.Dispatch<React.SetStateAction<boolean>>;
  sidebarOpen: boolean;
  setSidebarOpen: React.Dispatch<React.SetStateAction<boolean>>;
  // Derived
  activeLocation: LocationCoords | undefined;
  hasActiveFilters: () => boolean;
  getActiveFiltersCount: () => number;
  radiusSteps: number[];
  radiusBadgeCounts: Record<number, number>;
  totalItems: number;
  radiusCountsData: any;
  // Handlers
  quickSearch: string;
  handleSearchChange: (value: string) => void;
  handleResetAll: () => void;
  handlePriceFilterChange: (min: string, max: string) => void;
  setRadiusFromBadge: (km: number, count?: number) => void;
  ADMIN_LECCE_COORDS: { lat: number; lng: number };
}

export default function ItemListFilters({
  isDesktop,
  itemType,
  config,
  filters,
  setFilters,
  location,
  setLocation,
  locationStatus,
  setLocationStatus,
  radius,
  setRadius,
  useLocation,
  setUseLocation,
  useFixedLocation,
  setUseFixedLocation,
  availableOnly,
  setAvailableOnly,
  statusFilter,
  setStatusFilter,
  showFilters,
  setShowFilters,
  sidebarOpen,
  setSidebarOpen,
  activeLocation,
  hasActiveFilters,
  getActiveFiltersCount,
  radiusSteps,
  radiusBadgeCounts,
  totalItems,
  radiusCountsData,
  quickSearch,
  handleSearchChange,
  handleResetAll,
  handlePriceFilterChange,
  setRadiusFromBadge,
  ADMIN_LECCE_COORDS,
}: ItemListFiltersProps) {
  const { trackSearch } = useTracking();
  
  const handleFilterClick = () => {
    trackSearch('open_filters', filters, undefined).catch(console.error);
    setShowFilters(true);
  };
  
  // Desktop: sticky search bar + badges + filters row
  if (isDesktop) {
    return (
      <>
        <div className="sticky-search bg-white px-3 py-2 border-b border-gray-200">
          {/* Search Bar */}
          <NativeSearchBar
            placeholder={`Cerca ${config.plural.toLowerCase()}...`}
            value={quickSearch}
            onChange={handleSearchChange}
          />
          
          {/* Location Info */}
          {activeLocation && (
            <div className="flex items-center gap-1 text-sm text-gray-500 mt-1">
              <MapPin className="w-4 h-4" />
              <span className="truncate">{activeLocation.source}</span>
              <span className="text-blue-600 font-medium">{radius}km</span>
            </div>
          )}
          
          {/* Distance Badges + Filters Button */}
          <div className="mt-1 flex flex-col gap-1">
            {/* Row 1: Main filters */}
            <div className="flex gap-2 overflow-x-auto scrollbar-hide">
              <button
                onClick={() => setShowFilters(true)}
                className={`flex-shrink-0 flex items-center gap-2 px-3 py-1.5 text-xs font-medium bt-badge ${
                  hasActiveFilters()
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-blue-600 hover:bg-gray-200'
                }`}
              >
                <SlidersHorizontal className="w-4 h-4" data-testid="filter-button" />
                {hasActiveFilters() ? getActiveFiltersCount() : 'Filtri'}
              </button>
              
              <button
                onClick={() => { setAvailableOnly(!availableOnly); }}
                className={`flex-shrink-0 px-3 py-1.5 text-xs font-medium bt-badge transition-colors flex items-center gap-1.5 ${
                  availableOnly
                    ? 'bg-green-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <span className={`w-2 h-2 rounded-full ${availableOnly ? 'bg-white' : 'bg-gray-400'}`} />
                Solo disponibili
              </button>
            </div>
            
            {/* Row 2: Status filters */}
            {radiusCountsData?.status_counts && (
              <div className="flex gap-2 overflow-x-auto scrollbar-hide">
                <button
                  onClick={() => { setStatusFilter(''); }}
                  className={`flex-shrink-0 px-3 py-1.5 text-xs font-medium bt-badge transition-colors ${
                    statusFilter === ''
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  Tutti
                </button>
                <button
                  onClick={() => { setStatusFilter('available'); }}
                  className={`flex-shrink-0 px-3 py-1.5 text-xs font-medium bt-badge transition-colors flex items-center gap-1.5 ${
                    statusFilter === 'available'
                      ? 'bg-green-600 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <span className={`w-2 h-2 rounded-full ${statusFilter === 'available' ? 'bg-white' : 'bg-green-500'}`} />
                  Disponibili {radiusCountsData.status_counts.available !== undefined ? `(${radiusCountsData.status_counts.available})` : ''}
                </button>
                <button
                  onClick={() => { setStatusFilter('reserved'); }}
                  className={`flex-shrink-0 px-3 py-1.5 text-xs font-medium bt-badge transition-colors flex items-center gap-1.5 ${
                    statusFilter === 'reserved'
                      ? 'bg-yellow-500 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <span className={`w-2 h-2 rounded-full ${statusFilter === 'reserved' ? 'bg-white' : 'bg-yellow-500'}`} />
                  Prenotati {radiusCountsData.status_counts.reserved !== undefined ? `(${radiusCountsData.status_counts.reserved})` : ''}
                </button>
                <button
                  onClick={() => { setStatusFilter('sold'); }}
                  className={`flex-shrink-0 px-3 py-1.5 text-xs font-medium bt-badge transition-colors flex items-center gap-1.5 ${
                    statusFilter === 'sold'
                      ? 'bg-red-600 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <span className={`w-2 h-2 rounded-full ${statusFilter === 'sold' ? 'bg-white' : 'bg-red-500'}`} />
                  Venduti {radiusCountsData.status_counts.sold !== undefined ? `(${radiusCountsData.status_counts.sold})` : ''}
                </button>
              </div>
            )}
            
            {/* Row 3: Distance radius badges */}
            <div className="flex gap-2 overflow-x-auto scrollbar-hide">
              <button
                key="tutte"
                onClick={() => setRadiusFromBadge(-1, radiusCountsData?.total ?? totalItems)}
                className={`flex-shrink-0 px-3 py-1.5 text-xs font-medium bt-badge transition-colors ${
                  radius === -1
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                Tutte {radiusCountsData?.total ?? totalItems > 0 ? `(${radiusCountsData?.total ?? totalItems})` : ''}
              </button>
              {radiusSteps.map((km: number) => (
                <button
                  key={km}
                  onClick={() => setRadiusFromBadge(km, radiusBadgeCounts[km])}
                  className={`flex-shrink-0 px-3 py-1.5 text-xs font-medium bt-badge transition-colors ${
                    radius === km
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  ≤{km}km {radiusBadgeCounts[km] !== undefined ? `(${radiusBadgeCounts[km]})` : ''}
                </button>
              ))}
            </div>
          </div>
        </div>
        
        {/* Filter Bottom Sheet (also used on desktop) */}
        <FilterBottomSheet
          isOpen={showFilters}
          onClose={() => setShowFilters(false)}
          filters={filters}
          setFilters={setFilters}
          location={location}
          setLocation={setLocation}
          radius={radius}
          setRadius={setRadius}
          useLocation={useLocation}
          setUseLocation={setUseLocation}
          useFixedLocation={useFixedLocation}
          setUseFixedLocation={setUseFixedLocation}
          availableOnly={availableOnly}
          setAvailableOnly={setAvailableOnly}
          statusFilter={statusFilter}
          setStatusStatus={setStatusFilter}
          itemType={itemType}
          onReset={handleResetAll}
        />
      </>
    );
  }
  
  // Mobile layout: search bar + sticky filter bar + bottom sheet
  return (
    <>
      {/* Search Bar */}
      <div className="px-3 py-1">
        <NativeSearchBar
          placeholder={`Cerca ${config.plural.toLowerCase()}...`}
          value={quickSearch}
          onChange={handleSearchChange}
        />
      </div>
      
      {/* Sticky filter bar */}
      <div className="sticky-top z-20" style={{ backgroundColor: 'var(--color-surface)', borderBottom: '1px solid var(--color-border)' }}>
        <div className="px-3 py-1.5 flex items-center gap-2">
          {activeLocation && (
            <div className="flex items-center gap-1 text-[13px] flex-1" style={{ color: 'var(--color-text-muted)' }}>
              <MapPin className="w-3 h-3" />
              <span className="truncate">{activeLocation.source}</span>
              <span style={{ color: 'var(--color-primary)' }}>{radius}km</span>
            </div>
          )}
          
          <button
            onClick={handleFilterClick}
            className={`flex items-center gap-2 px-2.5 py-1.5 text-xs font-medium bt-badge ${
              hasActiveFilters()
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-blue-600'
            }`}
          >
            <SlidersHorizontal className="w-4 h-4" data-testid="filter-button" />
            {hasActiveFilters() ? getActiveFiltersCount() : 'Filtri'}
          </button>
        </div>
        
{/* Distance Badges */}
        <div className="px-3 pb-1.5 overflow-x-auto flex gap-2 scrollbar-hide">
          <button
            key="tutte"
            onClick={() => setRadiusFromBadge(-1, radiusCountsData?.total ?? totalItems)}
            className={`flex-shrink-0 px-2.5 py-1.5 text-xs font-medium bt-badge transition-colors ${
              radius === -1
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-600'
            }`}
          >
            Tutte {radiusCountsData?.total ?? totalItems > 0 ? `(${radiusCountsData?.total ?? totalItems})` : ''}
          </button>
          {radiusSteps.map((km: number) => (
            <button
              key={km}
              onClick={() => setRadiusFromBadge(km, radiusBadgeCounts[km])}
              className={`flex-shrink-0 px-2.5 py-1.5 text-xs font-medium bt-badge transition-colors ${
                radius === km
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-600'
              }`}
            >
              ≤{km}km {radiusBadgeCounts[km] !== undefined ? `(${radiusBadgeCounts[km]})` : ''}
            </button>
          ))}
        </div>
      </div>
      
      {/* Filter Bottom Sheet */}
      <FilterBottomSheet
        isOpen={showFilters}
        onClose={() => setShowFilters(false)}
        filters={filters}
        setFilters={setFilters}
        location={location}
        setLocation={setLocation}
        radius={radius}
        setRadius={setRadius}
        useLocation={useLocation}
        setUseLocation={setUseLocation}
        useFixedLocation={useFixedLocation}
        setUseFixedLocation={setUseFixedLocation}
        availableOnly={availableOnly}
        setAvailableOnly={setAvailableOnly}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        itemType={itemType}
        onReset={handleResetAll}
      />
    </>
  );
}
